import { Link } from 'react-router-dom';
import PlanMatrix from '../components/landing/PlanMatrix';
import CondoUnitsCalculator from '../components/landing/CondoUnitsCalculator';
// Hidden until paid-options launch (Jaime 2026-10-05 17:44) — flip to true + redeploy to restore.
const SHOW_UNITS_CALCULATOR = false;
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// ─── P0 website overhaul (marketing master plan 2026-10-09, APPROVED; build 2026-10-12) ──
// Admin-first hero, primary CTA "Explora la demo" → /login#demo (the EXISTING
// demo autosign section, "¿Quieres probar la app?" pills), secondary CTA anchor
// to pricing, sticky dismissible wedge banner + live-derived free-security
// wedge section, pricing honesty (decision B: no gateway naming), and
// hide-don't-eliminate flags for unverified content (constants/featureFlags.ts).
import { SOCIAL_PROOF_ENABLED, WAITLIST_ENABLED } from '../constants/featureFlags';
import { resolveApiBase } from '../lib/apiBase';
// V1-launch (2026-10-01): real app screenshots (captured from the running app) replace emoji visuals.
// 2026-10-01 rework: each feature ships BOTH a desktop (1440) and phone (390)
// capture with populated demo data; the layout picks the right one per breakpoint.
import shotQrDesktop from '../assets/landing/app-acceso-qr-desktop.webp';
import shotQrPhone from '../assets/landing/app-acceso-qr-phone.webp';
import shotFinanzasDesktop from '../assets/landing/app-reportes-financieros-desktop.webp';
import shotFinanzasPhone from '../assets/landing/app-reportes-financieros-phone.webp';
import shotComunidadDesktop from '../assets/landing/app-comunidad-desktop.webp';
import shotComunidadPhone from '../assets/landing/app-comunidad-phone.webp';
import shotPagosDesktop from '../assets/landing/app-pagos-desktop.webp';
import shotPagosPhone from '../assets/landing/app-pagos-phone.webp';

// ─── Feature Data ─────────────────────────────────────────────────────────────
// visual = REAL app screenshot captured from the running product (no emoji, no mocked UI).
const features = [
  {
    tag: 'Acceso',
    headline: 'Tu condominio,\ntu código QR.',
    body: 'Olvídate de las llaves. Accede con tu teléfono, recibe visitantes sin complicaciones y genera códigos temporales en segundos.',
    visualDesktop: shotQrDesktop,
    visualPhone: shotQrPhone,
    alt: 'Control de visitantes de Vecindus: registro de entradas y salidas',
  },
  {
    tag: 'Transparencia',
    headline: 'Reportes financieros\nen tiempo real.',
    body: 'Cada peso entra y sale documentado. Tus residentes ven los estados de cuenta claros; los morosos quedan visibles para todos.',
    visualDesktop: shotFinanzasDesktop,
    visualPhone: shotFinanzasPhone,
    alt: 'Reportes financieros de Vecindus: ingresos y egresos del condominio',
  },
  {
    tag: 'Comunidad',
    headline: 'Votaciones, eventos\ny avisos en un solo lugar.',
    body: 'Olvídate del WhatsApp. Anuncios oficiales, encuestas con quórum y calendario communal: todo organizado, todo a la vista.',
    visualDesktop: shotComunidadDesktop,
    visualPhone: shotComunidadPhone,
    alt: 'Módulo de comunidad de Vecindus: votaciones, avisos y calendario',
  },
  {
    tag: 'Cobranza',
    headline: 'Cobra en línea\nsin correr tras nadie.',
    // P0 §H pricing honesty (master plan decision B, 2026-10-10): NO gateway
    // naming (gateway name removed per master plan decision B); only methods
    // actually available for VE today.
    body: 'Cargos automáticos, recordatorios suaves y historial completo de pagos. Aceptamos tarjetas de crédito internacionales (Visa, Mastercard, AmEx); métodos locales en Venezuela: próximamente.',
    visualDesktop: shotPagosDesktop,
    visualPhone: shotPagosPhone,
    alt: 'Módulo de pagos de Vecindus: cargos pendientes e historial de pagos',
  },
];

// HIDDEN behind SOCIAL_PROOF_ENABLED (constants/featureFlags.ts) — hide-don't-
// eliminate (Jaime directive 2026-10-12): no real users exist yet, so these
// (fabricated-era) testimonials MUST NOT render. Code kept intact; re-enable
// ONLY with real, permission-cleared VE administrator quotes.
const testimonials = [
  { quote: 'Antes gastábamos 4 horas al mes en reportes. Ahora se hacen solos.', author: 'María G.', role: 'Administradora, CDMX' },
  { quote: 'Mis residentes ahora pagan a tiempo porque ven exactamente a dónde va su dinero.', author: 'Carlos R.', role: 'Presidente de condomino, Monterrey' },
  { quote: 'La villa estaba en crisis y todo era un desorden. Vecindus nos dio orden en 2 semanas.', author: 'Ana L.', role: 'Directora, Bogotá', highlight: true },
];

// ─── Live free-security wedge claims (P0 §E) ─────────────────────────────────
// Claims are derived ONLY from the live /api/public/plan-matrix (content
// contract: no hardcoded module claims). condo_free must report 'included'
// for these module ids; if the endpoint fails or a module is not included,
// NO module claim is rendered.
const WEDGE_MODULE_IDS = ['qr-access', 'panic'] as const;
const WEDGE_BANNER_DISMISS_KEY = 'vecindus-wedge-banner-dismissed';

interface PublicPlanCell { status: string; limit?: number | null; }
interface PublicPlan { id: string; name?: string; modules?: Record<string, PublicPlanCell>; }
interface PublicModule { id: string; label: string; }
interface PlanMatrixPublicData { plans?: PublicPlan[]; modules?: PublicModule[]; }

// ─── Component ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const { t } = useTranslation('common');
  const [isLoaded, setIsLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // P0 §0 sticky wedge banner: dismissible per browser session (reappears next session).
  const [wedgeBannerDismissed, setWedgeBannerDismissed] = useState(
    () => sessionStorage.getItem(WEDGE_BANNER_DISMISS_KEY) === '1'
  );
  // P0 §E: modules included in condo_free, straight from the live plan matrix.
  const [wedgeIncluded, setWedgeIncluded] = useState<{ id: string; label: string }[]>([]);

  // Launch-readiness item 1 (resumed 2026-10-01): pre-launch waiting list capture.
  // Contract (parallel feat/waitlist-api lane): POST /api/waitlist { email }
  //   -> 201 success | 409 duplicate | 400 invalid email. Plain 4xx handling only.
  // HIDDEN behind WAITLIST_ENABLED (constants/featureFlags.ts) — hide-don't-eliminate.
  const [wlEmail, setWlEmail] = useState('');
  const [wlStatus, setWlStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [wlMessage, setWlMessage] = useState('');

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${resolveApiBase()}/public/plan-matrix`, { headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = (await r.json()) as { data?: PlanMatrixPublicData };
        const d = json?.data;
        const free = d?.plans?.find(p => p.id === 'condo_free');
        const labelById = new Map((d?.modules ?? []).map(m => [m.id, m.label]));
        const included = WEDGE_MODULE_IDS
          .filter(id => free?.modules?.[id]?.status === 'included')
          .map(id => ({ id, label: labelById.get(id) ?? id }));
        if (!cancelled) setWedgeIncluded(included);
      } catch {
        if (!cancelled) setWedgeIncluded([]); // no live data → no module claims rendered
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function dismissWedgeBanner() {
    setWedgeBannerDismissed(true);
    try { sessionStorage.setItem(WEDGE_BANNER_DISMISS_KEY, '1'); } catch { /* storage unavailable */ }
  }

  async function submitWaitingList(e: React.FormEvent) {
    e.preventDefault();
    if (wlStatus === 'sending') return;
    setWlStatus('sending');
    setWlMessage('');
    try {
      const r = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: wlEmail.trim() }),
      });
      if (r.status === 201) {
        setWlStatus('done');
        return;
      }
      setWlStatus('error');
      if (r.status === 409) setWlMessage(t('waitlist.duplicate'));
      else if (r.status === 400) setWlMessage(t('waitlist.invalidEmail'));
      else setWlMessage(t('waitlist.genericError'));
    } catch {
      setWlStatus('error');
      setWlMessage(t('waitlist.genericError'));
    }
  }

  if (!isLoaded) {
    return <div className="min-h-full bg-white flex items-center justify-center text-gray-400">Cargando…</div>;
  }

  return (
    <div className="bg-white text-gray-900 font-sans" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* ── NAV ─────────────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200/50">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          {/* Logo — links to vecindus.com */}
          <a href="https://vecindus.com" className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
            <svg width="28" height="28" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="40" height="40" rx="10" fill="#36CCD8"/>
              <path d="M20 8L6 22L12 28L20 20L28 28L34 22L20 8Z" fill="white"/>
              <path d="M20 16L12 24L16 28L20 24L24 28L28 24L20 16Z" fill="white" fillOpacity="0.4"/>
            </svg>
            <span className="text-lg font-bold tracking-tight text-gray-900">Vecindus</span>
          </a>
          {/* Desktop nav — "Casos reales" only when real social proof exists */}
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-500">
            <a href="#features" className="hover:text-gray-900 transition-colors">Características</a>
            <a href="#pricing" className="hover:text-gray-900 transition-colors">Precios</a>
            {SOCIAL_PROOF_ENABLED && (
              <a href="#testimonials" className="hover:text-gray-900 transition-colors">Casos reales</a>
            )}
          </div>
          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-1">Iniciar Sesión</Link>
            <Link to="/register" className="bg-[#36CCD8] hover:bg-[#2bb8c5] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all">Comenzar</Link>
          </div>
          {/* Mobile hamburger */}
          <button className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/></svg>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-1">
            <a href="#features" className="block py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Características</a>
            <a href="#pricing" className="block py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Precios</a>
            {SOCIAL_PROOF_ENABLED && (
              <a href="#testimonials" className="block py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Casos reales</a>
            )}
            <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
              <Link to="/login" className="py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Iniciar Sesión</Link>
              <Link to="/register" className="bg-[#36CCD8] text-white text-sm font-semibold px-5 py-2.5 rounded-full text-center" onClick={() => setMobileMenuOpen(false)}>Comenzar</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ── STICKY WEDGE BANNER (P0 §0 — dismissible per session) ───────────── */}
      {!wedgeBannerDismissed && (
        <div className="fixed top-14 left-0 right-0 z-40 bg-[#2bb8c5] text-white shadow-md">
          <div className="max-w-6xl mx-auto px-6 py-2.5 flex items-center gap-3 text-sm">
            <a href="#seguridad-gratis" className="flex-1 text-center sm:text-left font-medium hover:underline underline-offset-2">
              Acceso QR y botón de pánico: <strong>gratis, siempre</strong>. Sin permanencia. Sin letra pequeña.
            </a>
            <button
              onClick={dismissWedgeBanner}
              aria-label="Cerrar aviso"
              className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* ── HERO (P0 §A — admin-first) ──────────────────────────────────────── */}
      <section className="pt-32 pb-24 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#36CCD8] text-sm font-semibold tracking-widest uppercase mb-5">Para administradores de condominios</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-tight mb-6">
            Administre su condominio<br/>sin perseguir morosos.
          </h1>
          <p className="text-xl sm:text-2xl text-gray-500 font-light max-w-xl mx-auto mb-10 leading-relaxed">
            Cobranza, comunicación y control de acceso en una sola app — sus residentes la usan a diario, usted la revisa una vez a la semana.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* P0 decision A: primary CTA routes to the EXISTING demo section
                ("¿Quieres probar la app?" autosign pills on /login, anchor #demo). */}
            <Link to="/login#demo" className="bg-[#36CCD8] hover:bg-[#2bb8c5] text-white font-semibold px-8 py-4 rounded-full text-lg transition-all shadow-lg shadow-[#36CCD8]/20">
              Explora la demo
            </Link>
            <a href="#pricing" className="border border-gray-300 hover:border-gray-400 text-gray-700 font-semibold px-8 py-4 rounded-full text-lg transition-all">
              Ver planes y precios
            </a>
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────────────── */}
      <section id="features" className="py-24 px-6 bg-gray-50 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          {features.map((f, i) => (
            <div key={i} className={`flex flex-col md:flex-row items-center gap-16 py-24 ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              {/* Text */}
              <div className="flex-1">
                <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4">{f.tag}</p>
                <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight whitespace-pre-line mb-6">{f.headline}</h2>
                <p className="text-xl text-gray-500 font-light leading-relaxed">{f.body}</p>
              </div>
              {/* Visual — real app screenshot: phone capture on mobile, desktop capture on md+ */}
              <div className="flex-1 flex justify-center">
                <div className="w-full max-w-md">
                  <img src={f.visualPhone} alt={f.alt} loading="lazy" className="md:hidden w-full max-w-[300px] mx-auto rounded-2xl border border-gray-200 shadow-xl shadow-gray-200/60" />
                  <img src={f.visualDesktop} alt={f.alt} loading="lazy" className="hidden md:block w-full rounded-2xl border border-gray-200 shadow-xl shadow-gray-200/60" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SOCIAL PROOF STRIP — HIDDEN (SOCIAL_PROOF_ENABLED=false): usage claim
             with zero real users. Code kept for re-enable with real data. ────── */}
      {SOCIAL_PROOF_ENABLED && (
        <section className="py-16 px-6 bg-[#36CCD8] text-white text-center">
          <div className="max-w-5xl mx-auto">
            <p className="text-white/70 text-sm font-medium tracking-widest uppercase mb-8">Usado por administradores en</p>
            <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-white/60 text-sm font-medium tracking-wide">
              {['México', 'Colombia', 'Venezuela', 'Perú', 'Chile', 'Argentina'].map(c => (
                <span key={c}>{c}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── TESTIMONIALS — HIDDEN (SOCIAL_PROOF_ENABLED=false): fabricated-era
             quotes with zero real users. Code kept; re-enable ONLY with real,
             permission-cleared VE administrator quotes. ──────────────────────── */}
      {SOCIAL_PROOF_ENABLED && (
        <section id="testimonials" className="py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4 text-center">Casos reales</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-center mb-16">Lo que dicen los administradores</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((t, i) => (
                <div key={i} className={`p-8 rounded-2xl border ${t.highlight ? 'border-[#36CCD8]/40 bg-[#36CCD8]/5' : 'border-gray-200 bg-white'}`}>
                  <p className="text-lg font-light text-gray-700 leading-relaxed mb-6">"{t.quote}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{t.author}</p>
                    <p className="text-sm text-gray-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FREE-SECURITY WEDGE (P0 §E — live-derived claims only) ───────────── */}
      <section id="seguridad-gratis" className="py-24 px-6 bg-[#36CCD8]/5 scroll-mt-24">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4">Seguridad</p>
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4">Seguridad no se cobra aparte.</h2>
          <p className="text-xl text-gray-500 font-light mb-10">Gratis, siempre — con o sin plan pagado.</p>
          {wedgeIncluded.length > 0 && (
            <ul className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-10 text-left">
              {wedgeIncluded.map(m => (
                <li key={m.id} className="flex items-start gap-3 p-5 rounded-2xl border border-[#36CCD8]/40 bg-white">
                  <svg className="w-5 h-5 mt-0.5 shrink-0 text-[#2bb8c5]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                  <span className="text-gray-700"><strong className="text-gray-900">{m.label}</strong> — incluido en el plan gratuito del condominio.</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/register" className="inline-block bg-[#36CCD8] hover:bg-[#2bb8c5] text-white font-semibold px-8 py-4 rounded-full text-lg transition-all shadow-lg shadow-[#36CCD8]/20">
            Active el nivel gratuito de su condominio hoy
          </Link>
        </div>
      </section>

      {/* ── PRICING (live-driven plan matrix, feat/landing-live-plan-table) ────────────── */}
      {/* Data comes from GET /api/public/plan-matrix (backend lane feat/public-plan-matrix).
          Live modules only (beta omitted server-side), limits visible per cell.
          Free plans: "Disponible" + CTA. Paid plans: "Próximamente", no prices in columns.
          P0 §H honesty (decision B): payment-method line states only methods actually
          available for VE today — no gateway naming. */}
      <section id="pricing" className="py-24 px-6 bg-gray-50 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4">Precios</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4">Simple y transparente</h2>
            <p className="text-xl text-gray-500 font-light">Compara lo que incluye cada plan, con sus límites visibles. Los planes de pago se anunciarán al lanzarlos.</p>
          </div>
          <PlanMatrix />
          <p className="text-center text-sm text-gray-500 mt-8">
            Aceptamos tarjetas de crédito internacionales (Visa, Mastercard, AmEx). Métodos locales en Venezuela: próximamente.
          </p>
          {/* Units calculator (feat/units-calculator): live brackets from
              /api/public/condo-pricing. Jaime (2026-10-05) explicitly authorized
              showing these prices HERE ONLY — plan columns above stay Próximamente
              with no prices. HIDDEN until paid-options launch (SHOW_UNITS_CALCULATOR). */}
          {SHOW_UNITS_CALCULATOR && <CondoUnitsCalculator />}
        </div>
      </section>


      {/* ── WAITING LIST — HIDDEN (WAITLIST_ENABLED=false): demo CTA replaced the
             waitlist funnel site-wide (P0). Code + /api/waitlist contract kept
             for a single-flag re-enable. ──────────────────────────────────────── */}
      {WAITLIST_ENABLED && (
        <section id="lista-de-espera" className="py-24 px-6 bg-gray-50">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4">{t('waitlist.badge')}</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4">{t('waitlist.title')}</h2>
            <p className="text-xl text-gray-500 font-light mb-10">{t('waitlist.subtitle')}</p>
            {wlStatus === 'done' ? (
              <div className="p-8 rounded-2xl border border-[#36CCD8]/40 bg-[#36CCD8]/5" aria-live="polite">
                <p className="text-5xl mb-4">🎉</p>
                <p className="text-lg font-semibold text-gray-900 mb-1">{t('waitlist.success')}</p>
                <p className="text-gray-500">{t('waitlist.successDetail')}</p>
              </div>
            ) : (
              <form onSubmit={submitWaitingList} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <label htmlFor="wl-email" className="sr-only">{t('waitlist.emailLabel')}</label>
                <input
                  id="wl-email"
                  type="email"
                  required
                  value={wlEmail}
                  onChange={e => setWlEmail(e.target.value)}
                  placeholder={t('waitlist.emailPlaceholder')}
                  className="flex-1 border border-gray-300 rounded-full px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#36CCD8]/40"
                />
                <button
                  type="submit"
                  disabled={wlStatus === 'sending'}
                  className="bg-[#36CCD8] hover:bg-[#2bb8c5] disabled:opacity-60 text-white font-semibold px-8 py-3.5 rounded-full text-sm transition-all shadow-lg shadow-[#36CCD8]/20 whitespace-nowrap"
                >
                  {wlStatus === 'sending' ? t('waitlist.submitting') : t('waitlist.submit')}
                </button>
              </form>
            )}
            {wlStatus === 'error' && <p className="text-sm text-red-500 mt-4" aria-live="polite">{wlMessage}</p>}
            {wlStatus !== 'done' && <p className="text-xs text-gray-400 mt-4">{t('waitlist.privacyNote')}</p>}
          </div>
        </section>
      )}

      {/* ── FINAL CTA ───────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-6">Tu condominio merece algo mejor.</h2>
          <p className="text-xl text-gray-500 font-light mb-10">Empieza hoy. Sin tarjeta, sin compromiso.</p>
          <Link to="/register" className="inline-block bg-[#36CCD8] hover:bg-[#2bb8c5] text-white font-semibold px-10 py-4 rounded-full text-lg transition-all shadow-lg shadow-[#36CCD8]/20">
            Crear mi condominio gratis
          </Link>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer className="border-t border-gray-200 py-12 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <svg width="30" height="30" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
              <rect width="40" height="40" rx="10" fill="#36CCD8"/>
              <path d="M20 8L6 22L12 28L20 20L28 28L34 22L20 8Z" fill="white"/>
              <path d="M20 16L12 24L16 28L20 24L24 28L28 24L20 16Z" fill="white" fillOpacity="0.4"/>
            </svg>
            <span className="font-semibold text-gray-900">Vecindus</span>
          </div>
          <p className="text-sm text-gray-400">© 2026 Vecindus. México · Colombia · Venezuela · Latinoamérica.</p>
          <p className="text-xs text-gray-400 mt-1">Contacto: <a href="mailto:info@vecindus.com" className="hover:text-gray-500 transition-colors">info@vecindus.com</a></p>
          <div className="flex gap-6 text-sm text-gray-500">
            <a href="/privacidad" className="hover:text-gray-900 transition-colors">Privacidad</a>
            <a href="/terminos" className="hover:text-gray-900 transition-colors">Términos</a>
            <a href="#" className="hover:text-gray-900 transition-colors">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
